import React, {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useRef,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CardMedia,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Modal,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import "../../../../css/calendar.css";
import {
  CommonFieldsData,
  DTOIframe,
  patientResearch,
  typology,
} from "../../../../networking";
import { ReservationSteppers } from "./ModalSteppers/ReservationSteppers";
import {
  ClassePriorita,
  Enti,
  PosizioneTicket,
} from "../../AppointmentManagement";
import dayjs from "dayjs";
import { DataContext } from "../Context/ContextWrapper";
import { CodicePostic } from "../Calendar";
import anagraficaIcon from "../../../../assets/Icons-desktop.svg";
import listaAttesaIcon from "../../../../assets/icons/Icon=ListBullets.svg";
import { Typology } from "../../../../services/interfaces";
import IframeContainer from "../../../IframeComponent/IframeContainer";

export interface Patient {
  CODICEFISCALE: string;
  COGNOME: string;
  DATANASCPAZIENTE: string;
  EMAIL: string;
  IDPAZIENTE: string;
  NOME: string;
  TELEFONO: string;
}

export type IframeParams = {
  type: string;
  data?: any;
  show: boolean;
};

const ModalTimer = (props: { handleTimeOut: () => void }) => {
  const context = useContext(DataContext);

  const [timeLeft, setTimeLeft] = useState(900);

  useEffect(() => {
    // se non esiste ancora una scadenza → la creo
    if (!context?.timeExpiry) {
      const expiry = Date.now() + timeLeft * 1000;
      context?.setTimeExpiry(expiry);
    } else {
      // ricalcolo sempre il tempo rimanente quando torno sulla pagina
      const newTimeLeft = Math.floor((context.timeExpiry - Date.now()) / 1000);
      setTimeLeft(newTimeLeft > 0 ? newTimeLeft : 0);
    }
  }, []);

  useEffect(() => {
    const timeExpiry = context?.timeExpiry;
    if (!timeExpiry) return;

    const timer = setInterval(() => {
      const newTimeLeft = Math.floor((timeExpiry - Date.now()) / 1000);

      if (newTimeLeft <= 0) {
        setTimeLeft(0);
        clearInterval(timer);
        props.handleTimeOut();
      } else {
        setTimeLeft(newTimeLeft);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [context?.timeExpiry]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box className="timeLeft">Tempo Rimanente: {formatTime(timeLeft)}</Box>
  );
};

// componente creata per ipotetica ottimizzazione, da revisionare
const TextFieldDurata = (props: {
  tempDurata: string;
  setTempDurata: (minuti: string) => void;
  disabled?: boolean;
}) => {
  return (
    <TextField
      fullWidth
      size="small"
      label="Durata minuti"
      variant="outlined"
      className="inputField"
      inputProps={{ maxLength: 2 }}
      defaultValue={props.tempDurata}
      disabled={props.disabled}
      onChange={(e) => {
        props.setTempDurata(e.target.value);
      }}
    />
  );
};

const MedicStep = (props: {
  selectedEvent?: any | null;
  tempDurata: string;
  setTempDurata: (minuti: string) => void;
  forzaturaAttiva: boolean;
  onToggleForzatura: () => void;
  typologyAgenda?: Typology[];
  data: string;
  setData: (data: string) => void;
  ora: string;
  setOra: (ora: string) => void;
  tipoAgenda: string;
  setTipoAgenda: (codAgenda: string) => void;
}) => {
  const disabled = !props.forzaturaAttiva;

  const options = props.typologyAgenda ?? [];

  return (
    <Grid
      container
      justifyContent="flex-start"
      spacing={2}
      marginBottom={3}
      marginTop={1}
    >
      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Prestazione"
          variant="outlined"
          className="inputField"
          value={`${props.selectedEvent?.descrizionePrestazione}`}
          disabled
        />
      </Grid>

      {/* Data appuntamento - ora e tipo agenda ora editabili con forzatura */}
      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Data Appuntamento"
          type="date"
          inputProps={{ max: "9999-12-31" }}
          variant="outlined"
          className="inputField"
          InputLabelProps={{ shrink: true }}
          value={props.data}
          onChange={(e) => props.setData(e.target.value)}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Importo (€)"
          variant="outlined"
          className="inputField"
          value={
            props.selectedEvent?.tariffa !== ""
              ? props.selectedEvent?.tariffa
              : "50.00"
          }
          disabled
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Azienda"
          variant="outlined"
          className="inputField"
          value={props.selectedEvent?.struttura ?? "ERRORE"}
          disabled
        />
      </Grid>

      <Grid item xs={4}>
        <Autocomplete
          options={options}
          size="small"
          getOptionLabel={(o) => o.DESCRIZIONE}
          isOptionEqualToValue={(a, b) => a.CODICE === b.CODICE}
          value={
            (props.typologyAgenda || []).find(
              (o) => o.CODICE === props.tipoAgenda
            ) || null
          }
          onChange={(_, v) => props.setTipoAgenda(v?.CODICE ?? "")}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tipo Agenda"
              variant="outlined"
              className="inputField"
            />
          )}
          noOptionsText="Nessuna tipologia disponibile"
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Ora Appuntamento"
          type="time"
          variant="outlined"
          className="inputField"
          value={props.ora}
          onChange={(e) => props.setOra(e.target.value)}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Operatore"
          variant="outlined"
          className="inputField"
          value={props.selectedEvent?.codiceOperatore}
          disabled
        />
      </Grid>

      <Grid item xs={4}>
        <TextFieldDurata
          tempDurata={props.tempDurata}
          setTempDurata={props.setTempDurata}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={4} alignContent={"center"}>
        <Button
          variant={
            props.forzaturaAttiva ? "forcingChecked" : "forcingUnchecked"
          }
          onClick={props.onToggleForzatura}
        >
          Forzatura
        </Button>
      </Grid>
    </Grid>
  );
};
//#region COMPONENTI STEP PAZIENTI
const PatientSearchBar = (props: {
  textInput: string;
  idPaziente: string;
  setTextInput: (minuti: string) => void;
  iframeStatus: IframeParams;
  setIframeStatus: (value: IframeParams) => void;
  handleResetPatientData: () => void;
  onSubmitSearchPatient: () => void;
  showInserisci?: boolean;
}) => {
  const ALPHA_NUMERIC_WITH_SPACES_REGEX = /^[a-zA-Z0-9\s]+$/;

  const context = useContext(DataContext);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("default error message");

  // UseEffect che controlla l'alfa numerico
  useEffect(() => {
    if (
      ALPHA_NUMERIC_WITH_SPACES_REGEX.test(props.textInput) ||
      props.textInput === ""
    ) {
      setError(false);
    } else {
      setErrorMessage("Attenzione sono richiesti solo caratteri alfanumerici");
      setError(true);
    }
  }, [props.textInput]);

  const handleSubmitSearchPatient = () => {
    if (props.textInput.length < 3) {
      setError(true);
      setErrorMessage("Sono richiesti un minimo di 3 caratteri");
      return;
    } else {
      setError(false);
      props.onSubmitSearchPatient();
    }
  };

  const disablingCheck = !props?.idPaziente || props?.idPaziente === "";

  return context?.statusAvanzamentoPrenotazione === 0 ? (
    <Grid
      container
      justifyContent="flex-start"
      spacing={2}
      marginTop={1}
      alignItems="center"
    >
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            variant="outlined"
            disabled={disablingCheck}
            onClick={() => {
              context.setTimestampAnagrafica({
                firstTimeStamp: dayjs().unix(),
                secondTimeStamp: null,
              });
              props?.setIframeStatus({
                type: "angrf",
                data: props.iframeStatus?.data ?? {},
                show: !props.iframeStatus?.show,
              });
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              "& img": { width: 18, height: 18 },
              "&:hover img": {
                filter: "brightness(0) invert(1)",
              },
            }}
          >
            <CardMedia
              component="img"
              className="icon-img"
              alt="icona anagrafica"
              sx={{ mr: 1, height: 20, width: 20 }}
              image={anagraficaIcon ?? <></>}
            />
            Anagrafica
          </Button>

          {/* <Button
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              "& img": { width: 18, height: 18 },
              "&:hover img": { filter: "brightness(0) invert(1)" },
            }}
          >
            <img src={listaAttesaIcon} alt="" />
            Lista Attesa
          </Button> */}

          <Button
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              "& img": { width: 18, height: 18 },
              "&:hover img": { filter: "brightness(0) invert(1)" },
            }}
          >
            <img src={anagraficaIcon} alt="" />
            Paziente Interno
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        {!error ? (
          <TextField
            fullWidth
            size="small"
            label="Ricerca Paziente"
            variant="outlined"
            className="inputField"
            placeholder="Nome, Cognome o Codice Fiscale"
            onChange={(e) => props.setTextInput(e.target.value)}
            value={props.textInput.trimStart() || ""}
            onKeyDown={(e) => {
              if (e.key === "Enter" && props.textInput.trim().length > 0) {
                handleSubmitSearchPatient();
              }
            }}
          />
        ) : (
          <TextField
            label="Errore"
            size="small"
            placeholder="Nome, Cognome o Codice Fiscale"
            fullWidth
            error
            helperText={errorMessage}
            onChange={(e) => props.setTextInput(e.target.value)}
            value={props.textInput.trimStart() || ""}
          />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="containedCustom"
              disabled={error}
              onClick={() => {
                if (props.textInput.length > 0) {
                  handleSubmitSearchPatient();
                }
              }}
            >
              Cerca
            </Button>
            <Button
              variant="outlinedError"
              onClick={props.handleResetPatientData}
            >
              Reset
            </Button>
          </Box>
          <Box sx={{ ml: "auto" }}>
            {props.showInserisci && (
              <Button
                variant="outlined"
                onClick={() =>
                  props?.setIframeStatus({
                    type: "insr",
                    data: props.iframeStatus?.data ?? {},
                    show: !props.iframeStatus?.show,
                  })
                }
              >
                Inserisci
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  ) : null;
};

const AlertNoDataFound = () => {
  return (
    <Box>
      <Alert
        severity="warning"
        sx={{
          border: "1px solid #e65100",
          marginTop: "20px",
          marginBottom: "20px",
          alignContent: "center",
          alignItems: "center",
          justifyContent: "flex-start",
          justifyItems: "flex-start",
          flexDirection: "row",
          "& .MuiAlert-icon": {
            marginLeft: 2,
            fontSize: 40,
          },
        }}
      >
        <Box>
          <Typography
            component="div"
            marginTop={"20px"}
            marginLeft={"20px"}
            marginRight={"10px"}
            marginBottom={"20px"}
            fontSize={"20px"}
            fontWeight={"bold"}
          >
            Non è stato trovato nessun risultato
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
};

// componente contenente la griglia con i campi popolati dopo la ricerca dei pazienti per codice fiscale, nome o cognome
const TablePatientsResults = (props: {
  rowsData: Patient[];
  setRowData: (patients: Patient[]) => void;
  setPazienteId: (id: string) => void;
  setNomeField: (nome: string) => void;
  setCognomeField: (cognome: string) => void;
  setDataNascita: (dataNascita: string) => void;
  setCodiceFiscale: (codiceFiscale: string) => void;
  setNumeroTelefono: (numeroTelefono: string) => void;
  setEmail: (email: string) => void;
  setPatientSearchSubmition: (value: boolean) => void;
  onClickRow: () => void;
}) => {
  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const result = props.rowsData.find((x: Patient) => {
      if (x.IDPAZIENTE === id) {
        return x;
      }
    });

    props.setPazienteId(result?.IDPAZIENTE ?? "");
    props.setNomeField(result?.NOME ?? "");
    props.setCognomeField(result?.COGNOME ?? "");
    props.setCodiceFiscale(result?.CODICEFISCALE ?? "");
    props.setDataNascita(result?.DATANASCPAZIENTE ?? "");
    props.setNumeroTelefono(result?.TELEFONO ?? "");
    props.setEmail(result?.EMAIL ?? "");

    props.setPatientSearchSubmition(false);
    props.setRowData([]);

    props.onClickRow();
  };

  const [page, setPage] = useState(0);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const visibleRows = useMemo(
    () =>
      [...props.rowsData]
        // .sort(getComparator(order, orderBy))
        .slice(page * 5, page * 5 + 5),
    // [order, orderBy, page, 5],
    [page, props.rowsData]
  );

  const headlines = [
    "Codice",
    "Cognome",
    "Nome",
    "Data di Nascita",
    "Codice Fiscale",
  ];

  if (props.rowsData.length > 0) {
    return (
      <TableContainer
        sx={{
          display: "block",
          borderRadius: "2px",
          borderTop: "1px solid #CCC",
          borderBottom: "1px solid #CCC",
          background: "#F9F9F9",
          marginBottom: "15px",
          maxHeight: "200px", //Ho messo 200px in modo che la tabella non esca fuori dal modale e contenga 5 righe visibili
          overflowY: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {headlines.map((element, key) => {
                return (
                  <TableCell
                    key={key}
                    sx={{
                      background: "#00000000",
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                      borderBottom: "1px solid #CCC",
                      color: "#000000",
                      fontStyle: "normal",
                    }}
                  >
                    {element}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row: Patient, index) => {
              let color;

              if (index % 2 === 0) {
                color = "#FFFFFF";
              } else {
                color = "#00000000";
              }

              return (
                <TableRow
                  key={row.IDPAZIENTE}
                  sx={{
                    background: color,
                  }}
                  hover
                  onClick={(event) => {
                    handleClick(event, row.IDPAZIENTE);
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                    }}
                  >
                    {row.IDPAZIENTE}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                    }}
                  >
                    {row.COGNOME}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                    }}
                  >
                    {row.NOME}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                    }}
                  >
                    {dayjs(row.DATANASCPAZIENTE).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #CCC",
                      borderLeft: "1px solid #CCC",
                    }}
                  >
                    {row.CODICEFISCALE}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          sx={{
            width: "100%",
            boxSizing: "border-box",
            borderTop: "1px solid #CCC",
            borderRight: "1px solid #CCC",
            borderLeft: "1px solid #CCC",
            justifySelf: "flex-end",
            justifyContent: "flex-end",
            justifyItems: "flex-end",
          }}
          count={props.rowsData.length}
          component="div"
          rowsPerPageOptions={[]}
          rowsPerPage={5}
          page={page}
          onPageChange={handleChangePage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} di ${count !== -1 ? count : `più di ${to}`}`
          }
        />
      </TableContainer>
    );
  } else {
    return <AlertNoDataFound />;
  }
};

const PatientStep = (props: {
  idPaziente: string;
  setPazienteId: (id: string) => void;
  iframeStatus?: IframeParams;
  setIframeStatus: (value: IframeParams) => void;
  areFieldsCompiled: boolean;
  setAreFieldsCompiled: Dispatch<SetStateAction<boolean>>;
  checkOnIdPaziente: () => boolean;
  nomePaziente: string;
  setNomeField: (nome: string) => void;
  cognomePaziente: string;
  setCognomeField: (cognome: string) => void;
  dataNascita: string;
  setDataNascita: (dataNascita: string) => void;
  codiceFiscale: string;
  setCodiceFiscale: (codiceFiscale: string) => void;
  numeroTelefono: string;
  setNumeroTelefono: (numeroTelefono: string) => void;
  email: string;
  setEmail: (email: string) => void;
}) => {
  const context = useContext(DataContext);

  useEffect(() => {
    if (
      props.nomePaziente &&
      props.cognomePaziente &&
      props.dataNascita &&
      props.codiceFiscale &&
      props.numeroTelefono
    ) {
      props.setAreFieldsCompiled(true);
    } else {
      props.setAreFieldsCompiled(false);
    }
  }, [props]);

  useEffect(() => {
    if (props.iframeStatus?.data) {
      props.setNomeField(props.iframeStatus.data.nomePaziente);
      props.setCognomeField(props.iframeStatus.data.cognomePaziente);
      props.setCodiceFiscale(props.iframeStatus.data.codiceFiscale);
      props.setDataNascita(props.iframeStatus.data.dataNascita);
      props.setEmail(props.iframeStatus.data.email);
      props.setNumeroTelefono(props.iframeStatus.data.telefono);
      props.setPazienteId(props.iframeStatus.data.idPaziente);

      props.setIframeStatus({ type: "", data: null, show: false });
    }

    if (props.areFieldsCompiled)
      context?.setPazienteDataSingolo({
        NOME: props.nomePaziente,
        COGNOME: props.cognomePaziente,
        CODICEFISCALE: props.codiceFiscale,
        EMAIL: props.email,
        DATANASCPAZIENTE: props.dataNascita,
        TELEFONO: props.numeroTelefono,
        IDPAZIENTE: props.idPaziente ?? "",
      });
  }, [props.areFieldsCompiled]);

  // controllo se il paziente è un privato o registrato
  const [isPatientPrivate, setIsPatientPrivate] = useState(false);

  // controllo se lo stato della prenotazione è ciclica ed imposta le dovute restrizione
  useEffect(() => {
    if (context?.statusAvanzamentoPrenotazione !== 0) {
      setIsPatientPrivate(true);
    } else {
      setIsPatientPrivate(props.checkOnIdPaziente());
    }
  }, [context?.statusAvanzamentoPrenotazione, props]);

  return (
    <Grid
      container
      justifyContent="flex-end"
      marginTop={-1.5}
      marginBottom={3}
      spacing={2}
    >
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Cognome"
          variant="outlined"
          className="inputField"
          disabled={isPatientPrivate}
          placeholder={"Inserire Cognome"}
          value={props.cognomePaziente}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => props.setCognomeField(e.target.value.trimStart())}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Nome"
          variant="outlined"
          className="inputField"
          disabled={isPatientPrivate}
          placeholder={"Inserire Nome"}
          value={props.nomePaziente}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => props.setNomeField(e.target.value.trimStart())}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
          label="Data di Nascita"
          type="date"
          inputProps={{ max: "9999-12-31" }}
          variant="outlined"
          className="inputField"
          disabled={isPatientPrivate}
          value={props.dataNascita}
          onChange={(e) => props.setDataNascita(e.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Codice Fiscale"
          placeholder={"Inserire Codice Fiscale"}
          variant="outlined"
          className="inputField"
          disabled={isPatientPrivate}
          InputLabelProps={{ shrink: true }}
          value={props.codiceFiscale}
          onChange={(e) => props.setCodiceFiscale(e.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Telefono"
          variant="outlined"
          className="inputField"
          InputLabelProps={{ shrink: true }}
          placeholder={"Inserire Telefono"}
          value={props.numeroTelefono}
          onChange={(e) => props.setNumeroTelefono(e.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Email"
          variant="outlined"
          className="inputField"
          placeholder={"Inserire Email"}
          InputLabelProps={{ shrink: true }}
          value={props.email}
          onChange={(e) => props.setEmail(e.target.value)}
        />
      </Grid>
    </Grid>
  );
};
//#endregion

//#region COMPONENTI STEP DI ACCETTAZIONE
const AcceptanceStep = (props: {
  iframeStatus?: IframeParams;
  setIframeStatus: (value: IframeParams) => void;
  checkOnIdPaziente: () => boolean;
  areFieldsCompiled: boolean;
  setAreFieldsCompiled: Dispatch<SetStateAction<boolean>>;
  posizioneTicket: string;
  setPosizioneTicket: Dispatch<SetStateAction<string>>;
  classePriorita: string;
  setClassePriorita: Dispatch<SetStateAction<string>>;
  numeroImpegnativa: string;
  setNumeroImpegnativa: Dispatch<SetStateAction<string>>;
  dataImpegnativa: string;
  setDataImpegnativa: Dispatch<SetStateAction<string>>;
  ente: string;
  setEnte: Dispatch<SetStateAction<string>>;
  numeroTessera: string;
  setNumeroTessera: Dispatch<SetStateAction<string>>;
  suggerita: boolean;
  setSuggerita: Dispatch<SetStateAction<boolean>>;
  primoAccesso: boolean;
  setPrimoAccesso: Dispatch<SetStateAction<boolean>>;
  tipoCalcolo: string;
  setTipoCalcolo: Dispatch<SetStateAction<string>>;
  rowsEntiData: Enti[];
  posizioneTicketDataByPrenotazione: CodicePostic[];
  rowsPosizioneTicketData: PosizioneTicket[];
  rowsClassePrioritaData: ClassePriorita[];
}) => {
  const context = useContext(DataContext);

  // controllo sul field "tipo calcolo" per definire comportamento view degli inputs accettabili
  const [required, setRequired] = useState<boolean>(false);

  const checkPosticMatch = (value: Enti) => {
    const matchedPosizioneTicket = props.rowsPosizioneTicketData.find(
      (e) => e.codice === value.codicepostic
    );
    if (matchedPosizioneTicket) {
      props.setPosizioneTicket(matchedPosizioneTicket.codice);
    }
  };


  // controllo se alla posizione ticket equivale ad un SSN
  useEffect(() => {
    const postick =
      props.rowsPosizioneTicketData.find(
        (posTick) => posTick?.codice === props.posizioneTicket
      ) || null;
    if (postick?.["descrizione regime"] === "SSN") {
      setRequired(true);
    } else {
      setRequired(false);
    }
  }, [props.posizioneTicket]);

  // controllo se campi necessari sono compilati o no
  useEffect(() => {
    if (required) {
      if (
        props.posizioneTicket &&
        props.posizioneTicket !== "" &&
        props.classePriorita &&
        props.classePriorita !== "" &&
        props.dataImpegnativa &&
        props.dataImpegnativa !== ""
      ) {
        props.setAreFieldsCompiled(true);
      } else {
        props.setAreFieldsCompiled(false);
      }
    } else {
      props.setAreFieldsCompiled(props.posizioneTicket ? true : false);
    }
  }, [props, required]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setSuggerita(event.target.checked);
  };

  const handleChangePrimoAccesso = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.setPrimoAccesso(event.target.checked);
  };

  return (
    <Grid
      container
      justifyContent="flex-start"
      spacing={2}
      marginBottom={3}
      marginTop={1}
    >
      {/* RIGA UNO */}
      <Grid item xs={4}>
        <FormControl fullWidth size="small">
          <Autocomplete
            options={props.rowsPosizioneTicketData}
            size="small"
            getOptionLabel={(o: PosizioneTicket) => {
              return `[${o.codice}] ${o.descrizione}`;
            }}
            isOptionEqualToValue={(a, b) => a.codice === b.codice}
            value={
              (props.rowsPosizioneTicketData || []).find(
                (o) => o.codice === props.posizioneTicket
              ) || null
            }
            onChange={(_, v) => props.setPosizioneTicket(v?.codice ?? "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Posizione Ticket"
                variant="outlined"
                className="inputField"
              />
            )}
            noOptionsText="Nessuna posizione ticket disponibile"
          />
        </FormControl>
      </Grid>
      <Grid item xs={8} />

      {/* RIGA DUE */}
      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Numero Impegnativa"
          required={required}
          helperText={required ? "Questo campo è richiesto" : null}
          variant="outlined"
          className="inputField"
          value={props.numeroImpegnativa}
          onChange={(e) => props.setNumeroImpegnativa(e.target.value)}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          size="small"
          label="Data Impegnativa"
          required={required}
          helperText={required ? "Questo campo è richiesto" : null}
          type="date"
          inputProps={{ max: "9999-12-31" }}
          variant="outlined"
          className="inputField"
          value={props.dataImpegnativa}
          onChange={(e) => props.setDataImpegnativa(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={4}>
        <FormControl fullWidth size="small">
          <Autocomplete
            options={props.rowsEntiData}
            autoHighlight
            size="small"
            getOptionLabel={(option) =>
              `${option["ragione sociale"]} [${option.codice}]`
            }
            renderInput={(params) => <TextField label="Ente" {...params} />}
            value={
              props.rowsEntiData.find(
                (option) => option.codice === props.ente
              ) || null
            }
            onChange={(_, value) => {
              props.setEnte(value ? value.codice : "");
              if (value) checkPosticMatch(value);
            }}
          />
        </FormControl>
      </Grid>

      {/* RIGA TRE */}
      <Grid item xs={4}>
        <FormControl fullWidth size="small">
          <Autocomplete
            options={props.rowsClassePrioritaData}
            autoHighlight
            size="small"
            getOptionLabel={(option) =>
              `${option.descrizione} [${option.codice}]`
            }
            renderInput={(params) => (
              <TextField
                label="Classe Priorità"
                required={required}
                helperText={required ? "Questo campo è richiesto" : null}
                {...params}
              />
            )}
            value={
              props.rowsClassePrioritaData.find(
                (option) => option.codice === props.classePriorita
              ) || null
            }
            onChange={(_, value) =>
              props.setClassePriorita(value ? value.codice : "")
            }
          />
        </FormControl>
      </Grid>
      {context?.apCardDettaglio.ablAPCARD !== "0" ? (
        <>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Numero Tessera"
              variant="outlined"
              className="inputField"
              value={props.numeroTessera}
              onChange={(e) => props.setNumeroTessera(e.target.value)}
              required={required}
              helperText={required ? "Questo campo è richiesto" : null}
            />
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", alignContent: "center" }}>
            <Button
              variant="contained"
              type="button"
              size="small"
              onClick={() =>
                props?.setIframeStatus({
                  type: "tssr",
                  data: props.iframeStatus?.data ?? {},
                  show: !props.iframeStatus?.show,
                })
              }
            >
              {`${context?.apCardDettaglio.titoloAPCARD}`}
            </Button>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={8} />
        </>
      )}

      {/* RIGA QUATTRO */}
      <Grid
        item
        xs={4}
        display={"flex"}
        justifyContent={"flex-start"}
        alignContent={"center"}
        gap={2}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={props.primoAccesso}
              onChange={handleChangePrimoAccesso}
            />
          }
          label={
            <Typography
              sx={{
                color: "#00000099",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              Primo accesso
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox checked={props.suggerita} onChange={handleChange} />
          }
          label={
            <Typography
              sx={{
                color: "#00000099",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              Suggerita
            </Typography>
          }
        />
      </Grid>
      <Grid item xs={8} />
    </Grid>
  );
};
//#endregion

//#region COMPONENTI STEP NOTE
const NotesStep = (props: {
  notePrenot: string;
  setNote: Dispatch<SetStateAction<string>>;
  distrettoNote: string;
  setDistrettoNote: Dispatch<SetStateAction<string>>;
  diagnosiNote: string;
  setDiagnosiNote: Dispatch<SetStateAction<string>>;
  prioritaNote: string;
  setPrioritaNote: Dispatch<SetStateAction<string>>;
}) => {
  return (
    <Grid
      container
      justifyContent="flex-start"
      spacing={2}
      marginBottom={3}
      marginTop={1}
    >
      <Grid item xs={6}>
        <NotesTextField note={props.notePrenot} setNote={props.setNote} />
      </Grid>

      <Grid item xs={6}>
        <PriorityLevelTextField
          prioritaNote={props.prioritaNote}
          setPrioritaNote={props.setPrioritaNote}
        />
      </Grid>

      <Grid item xs={6}>
        <DiagnosiTextField
          diagnosiNote={props.diagnosiNote}
          setDiagnosiNote={props.setDiagnosiNote}
        />
      </Grid>

      <Grid item xs={6}>
        <DistrettoTextField
          distrettoNote={props.distrettoNote}
          setDistrettoNote={props.setDistrettoNote}
        />
      </Grid>
    </Grid>
  );
};

const NotesTextField = (props: {
  note: string;
  setNote: Dispatch<SetStateAction<string>>;
}) => {
  const defaultPlaceholder = "Note Eventuali";

  const [textLength, setTextLength] = useState(0);

  const handleLength = (value: string) => {
    props.setNote(value);
    setTextLength(value.length);
  };

  return (
    <TextField
      fullWidth
      label="Note Prenotazione"
      variant="outlined"
      multiline
      inputProps={{ maxLength: 400 }}
      onChange={(e) => {
        handleLength(e.target.value);
      }}
      value={props.note || ""}
      helperText={`(${textLength}/400)`}
      className="inputField"
      rows={3}
      placeholder={defaultPlaceholder}
    />
  );
};

const PriorityLevelTextField = (props: {
  prioritaNote: string;
  setPrioritaNote: Dispatch<SetStateAction<string>>;
}) => {
  const defaultPlaceholder = "Descrizione delle note prestazione.";

  const [textLength, setTextLength] = useState(0);

  const handleLength = (value: string) => {
    props.setPrioritaNote(value);
    setTextLength(value.length);
  };

  return (
    <TextField
      fullWidth
      label="Note Prestazione"
      variant="outlined"
      multiline
      inputProps={{ maxLength: 200 }}
      onChange={(e) => handleLength(e.target.value)}
      value={props.prioritaNote || ""}
      helperText={`(${textLength}/200)`}
      className="inputField"
      rows={3}
      placeholder={defaultPlaceholder}
    />
  );
};

const DiagnosiTextField = (props: {
  diagnosiNote: string;
  setDiagnosiNote: Dispatch<SetStateAction<string>>;
}) => {
  const defaultPlaceholder = "Descrizione della diagnosi.";

  const [textLength, setTextLength] = useState(0);

  const handleLength = (value: string) => {
    props.setDiagnosiNote(value);
    setTextLength(value.length);
  };

  return (
    <TextField
      fullWidth
      label="Diagnosi"
      variant="outlined"
      multiline
      inputProps={{ maxLength: 250 }}
      onChange={(e) => handleLength(e.target.value)}
      value={props.diagnosiNote || ""}
      helperText={`(${textLength}/250)`}
      className="inputField"
      rows={3}
      placeholder={defaultPlaceholder}
    />
  );
};

const DistrettoTextField = (props: {
  distrettoNote: string;
  setDistrettoNote: Dispatch<SetStateAction<string>>;
}) => {
  const defaultPlaceholder =
    "Scrivere qui le informazioni relative al distretto";

  const [textLength, setTextLength] = useState(0);

  const handleLength = (value: string) => {
    props.setDistrettoNote(value);
    setTextLength(value.length);
  };

  return (
    <TextField
      fullWidth
      label="Distretto"
      variant="outlined"
      multiline
      inputProps={{ maxLength: 100 }}
      onChange={(e) => handleLength(e.target.value)}
      value={props.distrettoNote || ""}
      helperText={`(${textLength}/100)`}
      className="inputField"
      rows={3}
      placeholder={defaultPlaceholder}
    />
  );
};
//#endregion

const ModalReservation = (props: {
  handleSummary: (status: number) => Promise<void>;
  handleCicles: () => void;
  handleMultiplePrestazioni: () => void;
  descrizioneTipoAgenda: string | undefined;
  descrizionePrestazione: string | undefined;
  descrizioneSpecialita: string | undefined;
  open: boolean;
  onClose: () => void;
  onTimeout: () => void;
  onAppointmentAbort: (message: string) => void;
  bookingResponse: any | null;
  selectedEvent?: any | null;
  durata: string;
  setDurata: (minuti: string) => void;
  idPaziente: string;
  data: string;
  setData: (data: string) => void;
  ora: string;
  setOra: (ora: string) => void;
  tipoAgenda: string;
  setTipoAgenda: (codAgenda: string) => void;
  setIdPaziente: (id: string) => void;
  nomePaziente: string;
  setNomeField: (nome: string) => void;
  cognomePaziente: string;
  setCognomeField: (cognome: string) => void;
  dataNascita: string;
  setDataNascitaField: (dataNascita: string) => void;
  codiceFiscale: string;
  setCodiceFiscaleField: (codiceFiscale: string) => void;
  numeroTelefono: string;
  setNumeroTelefono: (numeroTelefono: string) => void;
  email: string;
  setEmail: (email: string) => void;
  posizioneTicket: string;
  setPosizioneTicket: Dispatch<SetStateAction<string>>;
  classePriorita: string;
  setClassePriorita: Dispatch<SetStateAction<string>>;
  numeroImpegnativa: string;
  setNumeroImpegnativa: Dispatch<SetStateAction<string>>;
  dataImpegnativa: string;
  setDataImpegnativa: Dispatch<SetStateAction<string>>;
  ente: string;
  setEnte: Dispatch<SetStateAction<string>>;
  numeroTessera: string;
  setNumeroTessera: Dispatch<SetStateAction<string>>;
  suggerita: boolean;
  setSuggerita: Dispatch<SetStateAction<boolean>>;
  primoAccesso: boolean;
  setPrimoAccesso: Dispatch<SetStateAction<boolean>>;
  tipoCalcolo: string;
  setTipoCalcolo: Dispatch<SetStateAction<string>>;
  note: string;
  setNote: Dispatch<SetStateAction<string>>;
  distrettoNote: string;
  setDistrettoNote: Dispatch<SetStateAction<string>>;
  diagnosiNote: string;
  setDiagnosiNote: Dispatch<SetStateAction<string>>;
  prioritaNote: string;
  setPrioritaNote: Dispatch<SetStateAction<string>>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  handleReset: () => void;
  tempDurata: string;
  setTempDurata: Dispatch<SetStateAction<string>>;
  rowsEntiData: Enti[];
  rowsPosizioneTicketData: PosizioneTicket[];
  posizioneTicketDataByPrenotazione: CodicePostic[];
  rowsClassePrioritaData: ClassePriorita[];
  // handleCancellation: () => void;
}) => {
  const [forzaturaAttiva, setForzaturaAttiva] = useState(false);

  const [textInput, setTextInput] = useState("");

  const [rowsData, setRowsData] = useState<Patient[]>([]);

  const [submittedPatientSearch, setPatientSearchSubmition] = useState(false);

  const [iframeStatus, setIframeStatus] = useState<IframeParams>({
    type: "",
    data: null,
    show: false,
  });

  const [areFieldsCompiled, setAreFieldsCompiled] = useState(false);

  const [showAlert, setShowAllert] = useState(false);

  const [typologyAgenda, setTypologyAgenda] = useState<Typology[]>([]);

  const [showInserisci, setShowInserisci] = useState(false);

  useEffect(() => {
    // controllo se i campi richiesti per l'inserimento sono compilati oppure no
    const tempBooleanCheck =
      !!(
        props?.nomePaziente &&
        props?.nomePaziente !== "" &&
        (props?.cognomePaziente || props?.cognomePaziente !== "") &&
        (!props?.idPaziente || props?.idPaziente === "")
      ) ||
      (submittedPatientSearch && rowsData.length <= 0);

    setShowInserisci(tempBooleanCheck);
  }, [
    props.nomePaziente,
    props.cognomePaziente,
    props.idPaziente,
    submittedPatientSearch,
    rowsData,
  ]);

  //Stato per inserire lo spinner in reservationStepper
  const [loadingSummary, setLoadingSummary] = useState(false);

  const handleAllert = () => {
    setShowAllert(!showAlert);
  };

  const context = useContext(DataContext);

  // controllo della presenza di un numero appuntamento assegnato in un context altrimenti provvede all'assegnazione
  useEffect(() => {
    if (!context?.numeroAppuntamento || null) {
      context?.setNumeroAppuntamento(
        props.bookingResponse &&
          props.bookingResponse.find((item: any) => item.id === "9")?.dato
      );
    }
  }, [props.bookingResponse]);

  useEffect(() => {
    const fetchTypology = async () => {
      const response = await typology({
        listino: "",
        medico: [],
        specialita: "",
        prestazione: "",
      });
      setTypologyAgenda(
        (response.data?.dati ?? []).filter((t: Typology) => t.CODICE)
      );
    };
    fetchTypology();
  }, []);

  // controllo assegnazione del numero appuntamento sulla componente di riepilo altrimenti provvede all'assegnazione
  useEffect(() => {
    if (!context?.numeroAppuntamentoRiepilogo || null) {
      context?.setNumeroAppuntamentoRiepilogo(
        props.bookingResponse &&
          props.bookingResponse.find((item: any) => item.id === "9")?.dato
      );
    }
  });

  // Controllo se è in corso una prenotazione multipla
  useEffect(() => {
    if (context?.statusAvanzamentoPrenotazione !== 0) {
      props.setActiveStep(2);

      //assegnazione dei dati del paziente
      props.setNomeField(context?.pazienteData?.NOME ?? "");
      props.setCognomeField(context?.pazienteData?.COGNOME ?? "");
      props.setDataNascitaField(
        context?.pazienteData?.DATANASCPAZIENTE ?? "1900/01/01"
      );
      props.setCodiceFiscaleField(context?.pazienteData?.CODICEFISCALE ?? "");
      props.setNumeroTelefono(context?.pazienteData?.TELEFONO ?? "");
      props.setEmail(context?.pazienteData?.EMAIL ?? "");
    }
    if (context?.ente) {
      props.setEnte(context.ente);
    }
  }, []);

  // Abilita automaticamente "Avanti" negli step che non richiedono validazione
  useEffect(() => {
    // Step 0 (Appuntamento) e Step 2 (Note) non hanno campi obbligatori
    if (props.activeStep === 0 || props.activeStep === 2) {
      setAreFieldsCompiled(true);
    }
  }, [props.activeStep]);

  const handleSummaryWithLoading = async (status: number) => {
    try {
      setLoadingSummary(true);
      await props.handleSummary(status);
    } finally {
      setLoadingSummary(false);
    }
  };

  // funzione per controllare se il paziente è stato prelevato da DB o se è nuovo
  const checkOnIdPazienteField = () => {
    return props.idPaziente ? true : false;
  };

  const fetchPatientData = async () => {
    const objectJsonRequest: CommonFieldsData = {
      ricerca: textInput.trimEnd(),
      dataNascita: "",
    };

    const objectJsonResponse = await patientResearch(objectJsonRequest);

    console.log(objectJsonResponse.data.dati);
    setRowsData(objectJsonResponse.data.dati);
  };

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [timerEnd, setTimerEnd] = useState(false);

  function startTimer(duration: number, callback: () => void): NodeJS.Timeout {
    const timeoutId = setTimeout(() => {
      callback();
    }, duration);
    return timeoutId;
  }

  // funzione che gestisce logica ai dati restituiti dalla submit del paziente OTTIMIZZABILE
  const handleSubmitSearchPatient = () => {
    setAreFieldsCompiled(false);
    setTimerEnd(false);
    // Se esiste un timer attivo, lo cancella prima di impostarne uno nuovo,
    // il timer era stato inserito per gestire lo stato della risposta della ricerca che era sempre 200
    if (timer) {
      clearTimeout(timer);
    }

    setPatientSearchSubmition(true);

    // Imposta un nuovo timer e salva il suo ID
    const newTimer = startTimer(10000, () => {
      setTimerEnd(true);
    });
    setTimer(newTimer); // Salva l'ID del timer nello stato

    if (rowsData.length > 0) {
      setRowsData([]);
    }

    fetchPatientData();
  };

  // funzione che gestisce logica coinvolta al click della ricerca di un paziente registrato
  const handleOnClickResearch = () => {
    setRowsData([]);
    setPatientSearchSubmition(false);
  };

  // funzione che "svuota" gli useStates esclusivamente del paziente
  const handleResetPatientData = () => {
    setIframeStatus({ type: "", data: null, show: false });
    props.setIdPaziente("");
    context?.setPazienteData(undefined);
    context?.setPazienteDataSingolo(undefined);
    props.setNomeField("");
    props.setCodiceFiscaleField("");
    props.setCognomeField("");
    props.setDataNascitaField("");
    props.setNumeroTelefono("");
    props.setEmail("");
    setRowsData([]);
    setPatientSearchSubmition(false);
    setTextInput("");
  };

  // funzione che "svuota" gli useStates valorizzati sulla modale
  const handleResetOnClose = () => {
    props.onAppointmentAbort("L'inserimento è stato annullato");

    if (context?.statusAvanzamentoPrenotazione === 0) {
      context?.setNumeroAppuntamento("");
    }

    // Step 1

    // Step 2
    setRowsData([]);
    setPatientSearchSubmition(false);
    setTextInput("");
    props.setIdPaziente("");
    props.setNomeField("");
    props.setCognomeField("");
    props.setDataNascitaField("");
    props.setNumeroTelefono("");
    props.setEmail("");
    props.setDataImpegnativa("");

    // Step 3
    props.setPosizioneTicket("");
    props.setNumeroImpegnativa("");
    props.setClassePriorita("");
    props.setNumeroTessera("");

    // Step 4
    props.setDiagnosiNote("");
    props.setDistrettoNote("");
    props.setNote("");
    props.setPrioritaNote("");
    // props.onClose();

    setForzaturaAttiva(false);
  };

  useEffect(() => {
    props.setData(props.selectedEvent?.data ?? "");
    props.setOra(props.selectedEvent?.ora ?? "");
    props.setTipoAgenda(props.selectedEvent?.tipoAgenda ?? "");
  }, [props.selectedEvent]);

  const prevStep = useRef(props.activeStep); // per memorizzare il valore precedente

  // aggiorna il valore precedente ogni volta che cambia lo step
  useEffect(() => {
    prevStep.current = props.activeStep;
  }, [props.activeStep]);

  // calcola la direzione in base al movimento
  const direction = props.activeStep > prevStep.current ? "left" : "right";

  return (
    <Dialog
      open={props.open}
      hideBackdrop
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: !iframeStatus.show
            ? "rgba(255, 255, 255, 0.7)"
            : "rgba(255, 255, 255)",
          boxShadow: "none",
          scrollbarWidth: "none",
        },
      }}
    >
      {!context?.numeroAppuntamento || loadingSummary ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 2000,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      ) : iframeStatus.show ? (
        <IframeContainer
          iframeStatus={iframeStatus}
          setIframeStatus={setIframeStatus}
          idPaziente={props.idPaziente}
        />
      ) : (
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "150vh",
            width: "90%",
            padding: 2,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Box className="modalHeader">
            <ModalTimer handleTimeOut={props.onTimeout} />
          </Box>
          <Grid container justifyContent="center" maxWidth="100%" mb={2}>
            {(!context.pazienteDataSingolo || props.activeStep <= 0) && (
              <>
                <Grid item xs={12} display="flex" justifyContent="flex-start">
                  <Typography
                    variant="h6"
                    component="div"
                    color="#01579B"
                    fontWeight="bold"
                  >
                    {`Prenotazione N° ${context.numeroAppuntamento}`}
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-start"
                  mt={1}
                >
                  <Typography
                    variant="h6"
                    fontSize="14px"
                    component="span"
                    color="#01579B"
                  >
                    Dott.:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontSize="14px"
                    component="span"
                    fontWeight="bold"
                    color="rgba(0, 0, 0, 0.5)"
                    sx={{ marginLeft: 0.5 }}
                  >
                    {context.medico ? context.medico : ""}
                  </Typography>
                </Grid>
              </>
            )}
            {(context.pazienteDataSingolo || context.pazienteData) &&
              props.activeStep >= 1 && (
                <>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-start"
                    mb={1}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color="#01579B"
                      fontWeight="bold"
                    >
                      {`Prenotazione N° ${context.numeroAppuntamento}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-start">
                    <Typography
                      variant="h6"
                      fontSize="14px"
                      component="span"
                      color="#01579B"
                      fontWeight="bold"
                    >
                      Dott.:
                    </Typography>
                    <Typography
                      variant="h6"
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                      color="rgba(0, 0, 0, 0.5)"
                      sx={{ marginLeft: 0.5 }}
                    >
                      {context.medico ? context.medico : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    {(context.pazienteData?.COGNOME ||
                      context.pazienteDataSingolo?.COGNOME) &&
                      (context.pazienteData?.NOME ||
                        context.pazienteDataSingolo?.NOME) && (
                        <>
                          <Typography
                            variant="h6"
                            fontSize="14px"
                            component="span"
                            color="#01579B"
                            fontWeight="bold"
                          >
                            Paziente:
                          </Typography>
                          <Typography
                            variant="h6"
                            fontSize="14px"
                            component="span"
                            fontWeight="bold"
                            color="rgba(0, 0, 0, 0.5)"
                            sx={{ marginLeft: 0.5 }}
                          >
                            {`${
                              context.pazienteData?.COGNOME ??
                              context.pazienteDataSingolo?.COGNOME ??
                              "N/A"
                            } 
                    ${
                      context.pazienteData?.NOME ??
                      context.pazienteDataSingolo?.NOME ??
                      ""
                    }`}
                          </Typography>
                        </>
                      )}
                  </Grid>
                </>
              )}

            {props.activeStep === 3 &&
              !showAlert &&
              context.statusAvanzamentoPrenotazione !== 3 && (
                <Grid
                  item
                  xs={12}
                  mt={2}
                  display={"flex"}
                  justifyContent={"flex-end"}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={props.handleCicles}
                    >
                      + Aggiungi Data
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={props.handleMultiplePrestazioni}
                    >
                      + Aggiungi Prestazione
                    </Button>
                  </Box>
                </Grid>
              )}
          </Grid>

          <ReservationSteppers
            activeStep={props.activeStep}
            showAlert={showAlert}
            handleAlert={handleAllert}
            setActiveStep={props.setActiveStep}
            areFieldsCompiled={areFieldsCompiled}
            handleSummary={handleSummaryWithLoading}
            handleClose={handleResetOnClose}
            loading={loadingSummary}
          >
            {props.activeStep === 0 && (
              <>
                <Slide
                  in={props.activeStep === 0}
                  direction={direction}
                  timeout={300}
                >
                  <Box
                    sx={{
                      marginTop: "12px",
                      marginBottom: "0px",
                      flexGrow: 10,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <MedicStep
                        forzaturaAttiva={forzaturaAttiva}
                        onToggleForzatura={() => setForzaturaAttiva((v) => !v)}
                        selectedEvent={props.selectedEvent}
                        tempDurata={props.tempDurata}
                        setTempDurata={props.setTempDurata}
                        typologyAgenda={typologyAgenda}
                        data={props.data}
                        setData={props.setData}
                        ora={props.ora}
                        setOra={props.setOra}
                        tipoAgenda={props.tipoAgenda}
                        setTipoAgenda={props.setTipoAgenda}
                      />
                    </Box>
                  </Box>
                </Slide>
              </>
            )}
            {props.activeStep === 1 && (
              <>
                <Slide
                  in={props.activeStep === 1}
                  direction={direction}
                  timeout={300}
                >
                  <Box
                    sx={{
                      marginTop: "12px",
                      marginBottom: "1px",
                      flexGrow: 10,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, marginBottom: "10px" }}>
                      <PatientSearchBar
                        textInput={textInput}
                        setTextInput={setTextInput}
                        handleResetPatientData={handleResetPatientData}
                        onSubmitSearchPatient={handleSubmitSearchPatient}
                        showInserisci={showInserisci}
                        iframeStatus={iframeStatus}
                        setIframeStatus={setIframeStatus}
                        idPaziente={props.idPaziente}
                      />
                    </Box>
                    {!(submittedPatientSearch && rowsData.length > 0) && (
                      <Divider sx={{ my: 2, bgcolor: "#D9D9D9", height: 1 }} />
                    )}
                    {timerEnd &&
                      submittedPatientSearch &&
                      rowsData.length <= 0 && <AlertNoDataFound />}
                    {!timerEnd &&
                      submittedPatientSearch &&
                      rowsData.length <= 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexGrow: 10,
                            marginBlock: "50px",
                            width: "100%",
                            justifyContent: "center",
                            justifyItems: "center",
                          }}
                        >
                          <CircularProgress size="6rem" />
                        </Box>
                      )}
                    {submittedPatientSearch && rowsData.length > 0 && (
                      <TablePatientsResults
                        rowsData={rowsData}
                        setRowData={setRowsData}
                        setPazienteId={props.setIdPaziente}
                        setNomeField={props.setNomeField}
                        setCognomeField={props.setCognomeField}
                        setCodiceFiscale={props.setCodiceFiscaleField}
                        setDataNascita={props.setDataNascitaField}
                        setNumeroTelefono={props.setNumeroTelefono}
                        setEmail={props.setEmail}
                        setPatientSearchSubmition={setPatientSearchSubmition}
                        onClickRow={handleOnClickResearch}
                      />
                    )}
                    {!submittedPatientSearch && (
                      <PatientStep
                        iframeStatus={iframeStatus}
                        setIframeStatus={setIframeStatus}
                        areFieldsCompiled={areFieldsCompiled}
                        setAreFieldsCompiled={setAreFieldsCompiled}
                        idPaziente={props.idPaziente}
                        setPazienteId={props.setIdPaziente}
                        nomePaziente={props.nomePaziente}
                        setNomeField={props.setNomeField}
                        cognomePaziente={props.cognomePaziente}
                        setCognomeField={props.setCognomeField}
                        dataNascita={props.dataNascita}
                        setDataNascita={props.setDataNascitaField}
                        codiceFiscale={props.codiceFiscale}
                        setCodiceFiscale={props.setCodiceFiscaleField}
                        numeroTelefono={props.numeroTelefono}
                        email={props.email}
                        setEmail={props.setEmail}
                        setNumeroTelefono={props.setNumeroTelefono}
                        checkOnIdPaziente={checkOnIdPazienteField}
                      />
                    )}
                  </Box>
                </Slide>
              </>
            )}
            {props.activeStep === 3 && (
              <>
                <Slide
                  in={props.activeStep === 3}
                  direction={direction}
                  timeout={300}
                >
                  <Box
                    sx={{
                      marginTop: "12px",
                      marginBottom: "0px",
                      flexGrow: 10,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <AcceptanceStep
                        iframeStatus={iframeStatus}
                        setIframeStatus={setIframeStatus}
                        areFieldsCompiled={areFieldsCompiled}
                        setAreFieldsCompiled={setAreFieldsCompiled}
                        checkOnIdPaziente={checkOnIdPazienteField}
                        posizioneTicket={props.posizioneTicket}
                        setPosizioneTicket={props.setPosizioneTicket}
                        classePriorita={props.classePriorita}
                        setClassePriorita={props.setClassePriorita}
                        numeroImpegnativa={props.numeroImpegnativa}
                        setNumeroImpegnativa={props.setNumeroImpegnativa}
                        dataImpegnativa={props.dataImpegnativa}
                        setDataImpegnativa={props.setDataImpegnativa}
                        rowsEntiData={props.rowsEntiData}
                        posizioneTicketDataByPrenotazione={
                          props.posizioneTicketDataByPrenotazione
                        }
                        rowsPosizioneTicketData={props.rowsPosizioneTicketData}
                        rowsClassePrioritaData={props.rowsClassePrioritaData}
                        ente={props.ente}
                        setEnte={props.setEnte}
                        numeroTessera={props.numeroTessera}
                        setNumeroTessera={props.setNumeroTessera}
                        suggerita={props.suggerita}
                        setSuggerita={props.setSuggerita}
                        primoAccesso={props.primoAccesso}
                        setPrimoAccesso={props.setPrimoAccesso}
                        tipoCalcolo={props.tipoCalcolo}
                        setTipoCalcolo={props.setTipoCalcolo}
                      />
                    </Box>
                  </Box>
                </Slide>
              </>
            )}
            {props.activeStep === 2 && (
              <>
                <Slide
                  in={props.activeStep === 2}
                  direction={direction}
                  timeout={300}
                >
                  <Box
                    sx={{
                      marginTop: "12px",
                      marginBottom: "0px",
                      flexGrow: 10,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <NotesStep
                        notePrenot={props.note}
                        setNote={props.setNote}
                        prioritaNote={props.prioritaNote}
                        setPrioritaNote={props.setPrioritaNote}
                        diagnosiNote={props.diagnosiNote}
                        setDiagnosiNote={props.setDiagnosiNote}
                        distrettoNote={props.distrettoNote}
                        setDistrettoNote={props.setDistrettoNote}
                      />
                    </Box>
                  </Box>
                </Slide>
              </>
            )}
          </ReservationSteppers>
        </Box>
      )}
    </Dialog>
  );
};

export default ModalReservation;
